export type PartnersImage = { url: string; alt: string };

/** Nadtitulok, nadpis a úvod sekcie. `lead` je HTML z editora administrácie. */
export type PartnersHead = { eyebrow: string; title: string; lead: string };

export type PartnersAudience = {
  id: string;
  label: string;
  icon: string;
  title: string;
  text: string;
  quote: string;
  points: string[];
  tags: string[];
  image: PartnersImage | null;
};

export type PartnersModel = {
  name: string;
  title: string;
  text: string;
  ownerTasks: string[];
  partnerTasks: string[];
  fit: string;
  image: PartnersImage | null;
};

export type PartnersSplitRow = {
  step: string;
  cells: [PartnersSplitCell, PartnersSplitCell];
};

export type PartnersSplitCell = { text: string; highlight: boolean };

/** Kľúč modelu, ktorý zvýrazňuje prepínač aj stĺpec tabuľky: prvý model = `a`, druhý = `b`. */
export type PartnersModelKey = 'a' | 'b';

/**
 * Tu, nie v `partners-models.tsx`: hodnota exportovaná z 'use client' modulu je v serverovom
 * komponente len klientska referencia, nie pole.
 */
export const MODEL_KEYS: PartnersModelKey[] = ['a', 'b'];

export type PartnersAdvisorQuestion = {
  id: string;
  question: string;
  /** Poradie odpovedí: za model B (−1), nerozhodná (0), za model A (+1). */
  answers: [string, string, string];
};

export type PartnersAdvice = { title: string; text: string };

export type PartnersAdvisor = {
  questions: PartnersAdvisorQuestion[];
  tag: string;
  initial: PartnersAdvice;
  link: string;
  outcomes: Record<PartnersModelKey | 'mix', PartnersAdvice>;
};

export type PartnersCard = { title: string; text: string; icon: string };

export type PartnersProduct = {
  title: string;
  text: string;
  href: string;
  image: PartnersImage | null;
};

export type PartnersStep = { title: string; time: string; text: string };

export type PartnersRegion = { name: string; cities: { href: string; label: string }[] };

export type PartnersFaq = { question: string; answer: string };

export type PartnersFormContent = {
  partnerTypeLabel: string;
  partnerTypes: string[];
  assemblyLabel: string;
  assemblyOptions: string[];
  placeLabel: string;
  placePlaceholder: string;
  messageLabel: string;
  messagePlaceholder: string;
  consent: string;
  note: string;
  error: string;
  fileTypeError: string;
  fileSizeError: string;
  subject: string;
};

/** Texty univerzálnych prekladov (`t()`), ktoré stránka používa. */
export type PartnersLabels = {
  home: string;
  step: string;
  name: string;
  company: string;
  email: string;
  submit: string;
  sending: string;
  attachments: string;
  chooseFiles: string;
  removeFile: string;
  uploadError: string;
};

export type PartnersPageData = {
  name: string;
  a11y: { breadcrumb: string; audienceTabs: string; modelSwitch: string; label: string };
  hero: {
    image: PartnersImage | null;
    eyebrow: string;
    title: string;
    titleAccent: string;
    lead: string;
    primaryCta: string;
    secondaryCta: string;
    facts: { value: string; label: string }[];
  };
  audiences: PartnersHead & { items: PartnersAudience[] };
  models: PartnersHead & { ownerLabel: string; partnerLabel: string; items: PartnersModel[] };
  split: PartnersHead & { hint: string; rows: PartnersSplitRow[] };
  advisor: PartnersHead & PartnersAdvisor;
  products: PartnersHead & { items: PartnersProduct[] };
  labelling: PartnersHead & {
    note: string;
    sampleTag: string;
    rows: { label: string; value: string }[];
    image: PartnersImage | null;
  };
  steps: PartnersHead & { items: PartnersStep[] };
  developers: PartnersHead & { items: { title: string; text: string }[] };
  documents: PartnersHead & {
    items: PartnersCard[];
    equipment: { title: string; items: string[]; note: string };
  };
  promises: PartnersHead & { items: PartnersCard[] };
  regions: PartnersHead & { items: PartnersRegion[] };
  faq: PartnersHead & { items: PartnersFaq[] };
  contact: PartnersHead & { benefits: string[]; form: PartnersFormContent };
};
